import type React from "react";
import {ButtonBase, Stack, Typography} from "@mui/material";
import {ExpandLess, ExpandMore} from "@mui/icons-material";
import {
    type TierControl,
    tierLabel,
} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/valueChainLayout";
import type {ValueChainDirection} from "~/pages/supplier-360/tabs/SupplierValueChain/valueChainLinkTypes";

const DIRECTIONS: Array<{direction: ValueChainDirection; label: string}> = [
    {direction: 'upstream', label: 'Upstream'},
    {direction: 'downstream', label: 'Downstream'},
];

/**
 * Collapse / expand each tier (tier 2 on) from a fixed spot on the canvas. Styled like the
 * supply-type filter beside it.
 */
export const ChainTierToggles: React.FC<{controls: TierControl[]}> = ({controls}) => {
    return (
        <Stack gap={0.5} sx={{p: '4px', bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.100', borderRadius: '8px'}}>
            {DIRECTIONS.map(({direction, label}) => {
                const tiers = controls.filter(control => control.direction === direction);
                if (tiers.length === 0) return null;
                return (
                    <Stack key={direction} direction="row" alignItems="center" gap="2px" role="group" aria-label={`${label} tiers`}>
                        <Typography variant="caption" color="text.secondary" sx={{width: 72, pl: 0.5}}>
                            {label}
                        </Typography>
                        {tiers.map(control => {
                            const Icon = control.collapsed ? ExpandMore : ExpandLess;
                            const name = tierLabel(control.tier);
                            return (
                                <ButtonBase
                                    key={control.tier}
                                    onClick={control.onToggle}
                                    aria-expanded={!control.collapsed}
                                    aria-label={`${control.collapsed ? 'Expand' : 'Collapse'} ${label} ${name}`}
                                    sx={{
                                        height: 26,
                                        px: 1,
                                        gap: 0.25,
                                        borderRadius: '6px',
                                        color: control.collapsed ? 'text.secondary' : 'primary.main',
                                        bgcolor: control.collapsed ? 'transparent' : 'common.white',
                                        boxShadow: control.collapsed ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.08)',
                                        '&:hover': {color: 'text.primary'},
                                    }}
                                >
                                    <Typography fontSize={12} fontWeight={600} color="inherit" noWrap>{name}</Typography>
                                    <Icon sx={{fontSize: 16}}/>
                                </ButtonBase>
                            );
                        })}
                    </Stack>
                );
            })}
        </Stack>
    );
};
